import { useEffect, useState, useCallback, useMemo } from "react";
import Select from "react-select";
import { toast } from "sonner";
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { useParams } from "react-router-dom";
import VariantSelector from '../../components/VariantSelector';
import { Edit, Save, X, Loader2, CheckCircle, AlertCircle, 
          Package, Phone, Trash2,
          Headset
          } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { statuses } from "../../cors/statusMap";
import MapDirection from "../../cors/MapDirection";


export default function OrderUpdate() {
  const { t, locale } = useLanguage();
  const { id } = useParams();
  const { token , role, roles } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cities, setCities] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  
  const statusOptions = [
    { value: 'refuse', label: t('status.refuse') },
    { value: 'annule', label: t('status.annule') },
    { value: 'reporte', label: t('status.reporte') },
    { value: 'nonconfirme', label: t('status.nonconfirme') },
    { value: 'retour', label: t('status.retour') },
    { value: 'nouveau', label: t('status.nouveau') },
    { value: 'confirme', label: t('status.confirme') },
    { value: 'en livraison', label: t('status.indelivery') },
    { value: 'livre', label: t('status.livre') },
    { value: 'Livre(paye)', label: t('status.livre_paye') },
    { value: 'retour_recu', label: t('status.retour_recu') },
  ];

  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200";
  const getStatusText = (status) => statuses[status]?.text || status;
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon;
    return Icon ? <Icon size={iconSize} /> : null;
  };

  const [formData, setFormData] = useState({
    status: '',
    delivery_price: 0,
    change_price: 0,
    comment: '',
    city: null,
    unlockable: false,
    change: false,
    client: {
      name: '',
      phone: '',
      address: '',
      city: ''
    },
    products: []
  });

  const { subtotal, total, profit } = useMemo(() => {
    if (!order) return { subtotal: 0, total: 0, profit: 0 };
    
    const subtotal = formData.products.reduce((sum, product) => 
      sum + (product.quantity * product.selling_price), 0);
    
    const total = subtotal ;
    
    const productsProfit = formData.products.reduce((sum, product) => 
      sum + (product.quantity * (product.selling_price - product.unit_price)), 0);
    
    const finalProfit = productsProfit - formData.delivery_price;
    
    return { 
      subtotal: parseFloat(subtotal.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      profit: parseFloat(finalProfit.toFixed(2))
    };
  }, [order, formData.delivery_price, formData.products, formData.change]);

  const formatVariants = useCallback((variants) => {
    if (!variants || variants.length === 0) return [];
    
    return variants.map(variantGroup => {
      return variantGroup.values.map(variant => ({
        ...variant,
        type: variantGroup.type
      }));
    }).flat();
  }, []);

  const transformAllVariants = useCallback((allvariants) => {
    if (!allvariants) return [];
    
    return allvariants.map(variantGroup => ({
      id: variantGroup.id,
      type: variantGroup.details[0]?.variant.type || '',
      details: variantGroup.details.map(detail => ({
        variant: {
          id: detail.variant.id,
          name: detail.variant.name,
          value: detail.variant.value,
          type: detail.variant.type
        }
      }))
    }));
  }, []);


  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // First load cities
        const citiesResponse = await fetch(`${BASE_URL}/api/getallcities`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        const citiesData = await citiesResponse.json();
        setCities(citiesData?.data || []);
        
        // Then load order with city data
        const orderResponse = await fetch(`${BASE_URL}/api/orders/${id}`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        const orderData = await orderResponse.json();
        
        
        if (!orderResponse.ok) throw new Error(orderData.message || t('orders.fetch_failed'));
        
        // Find city data
        const cityData = citiesData.data.find(c => c.name === orderData.order.client.city) || {
          deliver: orderData.order.delivery_price,
          change: orderData.order.change_price || orderData.order.delivery_price
        };
        
        setOrder(orderData.order);
        setFormData({
          status: orderData.order.status,
          delivery_price: parseFloat(orderData.order.delivery_price),
          change_price: parseFloat(orderData.order.change_price || orderData.order.delivery_price),
          comment: orderData.order.commentaire,
          city: { 
            label: orderData.order.client.city,
            value: orderData.order.client.city,
            deliver: parseFloat(cityData.deliver) || 0,
            change: parseFloat(cityData.change) || 0
          },
          change: Boolean(Number(orderData.order.change)),
          unlockable: Boolean(Number(orderData.order.unlockable)),
          client: {
            name: orderData.order.client.name,
            phone: orderData.order.client.phone,
            address: orderData.order.client.address,
            city: orderData.order.client.city
          },
          products: orderData.order.products.map(product => {
            const selectedVariants = {};
            product.variants.forEach(variantGroup => {
              variantGroup.values.forEach(variant => {
                selectedVariants[variantGroup.type] = {
                  id: variant.id,
                  name: variant.name,
                  value: variant.value,
                  type: variantGroup.type
                };
              });
            });

            return {
              id: product.id,
              name: product.name,
              image_py: product.image_py,
              quantity: product.quantity,
              unit_price: parseFloat(product.unit_price),
              selling_price: parseFloat(product.selling_price),
              variants: product.variants,
              allvariants: product.allvariants,
              selectedVariants: selectedVariants
            };
          })
        });
        
      
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    
      loadData();
      
  }, [id, token]);

  const fetchData = async () => {
        // Finally load products
      const productsResponse = await fetch(`${BASE_URL}/api/products`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "type": "update"
        },
      });
      const productsData = await productsResponse.json();
      setAvailableProducts(productsData?.data || []);
  }

  const cityOptions = useMemo(() => 
    cities.map(city => ({
      value: city.name,
      label: city.name,
      deliver: parseFloat(city.deliver) || 0,
      change: parseFloat(city.change) || 0
    }))
  , [cities]);

  const productOptions = useMemo(() => 
    availableProducts.map(product => ({
      value: product.id,
      label: product.name,
      data: product
    }))
  , [availableProducts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        [name]: value
      }
    }));
  };
  
  const handleProductChange = (index, field, value) => {
    setFormData(prev => {
      const updatedProducts = [...prev.products];
      let numericValue;
      
      if (field === 'quantity') {
        numericValue = parseInt(value) || 0;
        numericValue = Math.max(1, numericValue);
      } else {
        numericValue = parseFloat(value) || 0;
        numericValue = Math.max(0, numericValue);
      }
      
      updatedProducts[index] = {
        ...updatedProducts[index],
        [field]: numericValue
      };
      
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };

  const handleVariantChange = (selectedVariants, productIndex) => {
    setFormData(prev => {
      const updatedProducts = [...prev.products];
      updatedProducts[productIndex] = {
        ...updatedProducts[productIndex],
        selectedVariants: selectedVariants
      };
      
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };


  const handleCityChange = (selectedOption) => {
    if (!selectedOption) return;
    
    setFormData(prev => {
      const newDeliveryPrice = prev.change 
        ? (selectedOption?.change || 0)
        : (selectedOption?.deliver || 0);
      
      return {
        ...prev,
        city: selectedOption,
        delivery_price: newDeliveryPrice,
        change_price: selectedOption?.change || 0,
        client: {
          ...prev.client,
          city: selectedOption?.label || ''
        }
      };
    });
  };

  const handleStatusChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, status: selectedOption.value }));
  };

  const handleChangeToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData(prev => {
      if (!prev.city) return prev;
      
      const newDeliveryPrice = isChecked 
        ? (prev.city?.change || 0)
        : (prev.city?.deliver || 0);
      
      return {
        ...prev,
        change: isChecked,
        delivery_price: newDeliveryPrice,
        change_price: prev.city?.change || 0
      };
    });
  };

  const handleAddProduct = (selectedOption) => {
    if (!selectedOption) return;
    
    const product = selectedOption.data;
    console.log(product);
    const now = new Date();
    console.log(now);
    
    const isDiscountValid = (
      product.discount && 
      (product.discount.status === true || product.discount.status === "true") && // Supports both boolean and string
      !isNaN(new Date(product.discount.date_start)) && // Ensure valid date
      !isNaN(new Date(product.discount.date_end)) &&
      new Date(product.discount.date_start) <= now &&
      new Date(product.discount.date_end) >= now
    );

    const originalPrice = parseFloat(product.price.saller_price) || 0;
    const discountValue = (product.discount?.value || 0) / 100;
    const unitPrice = isDiscountValid 
      ? originalPrice * (1 - discountValue) 
      : originalPrice;
    
    const newProduct = {
      id: product.id,
      name: product.name,
      image_py: product.image_py,
      quantity: 1,
      unit_price: unitPrice,
      selling_price: parseFloat(product.price.selling_price),
      variants: [],
      allvariants: product.variants || [],
      selectedVariants: {}
    };
    
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => {
      const updatedProducts = [...prev.products];
      updatedProducts.splice(index, 1);
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };  

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validate variants before submission
  for (let i = 0; i < formData.products.length; i++) {
    const product = formData.products[i];
    
    if (product.allvariants && product.allvariants.length > 0) {
      const requiredVariantTypes = Array.from(
        new Set(
          product.allvariants.flatMap(group => 
            group.details.map(detail => detail.variant.type)
          )
        )
      );

      // Check if all required variants are selected
      const missingTypes = requiredVariantTypes.filter(
        type => !product.selectedVariants?.[type]
      );

      if (missingTypes.length > 0) {
        const missingTypesText = missingTypes.map(t => `"${t}"`).join(', ');
        toast.error(
          `${t('orders.variant_required')} ${missingTypesText}`,
          { duration: 5000 }
        );
        
        // Highlight the problematic product
        const element = document.getElementById(`product-${i}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          element.classList.add('ring-2', 'ring-red-500');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-red-500');
          }, 3000);
        }
        
        return;
      }

      // Check if variant combination is valid
      const isValidCombination = product.allvariants.some(group =>
        group.details.every(detail =>
          product.selectedVariants[detail.variant.type]?.id === detail.variant.id
        )
      );

      if (!isValidCombination) {
        toast.error(
          t('orders.variant_not_available'),
          { duration: 5000 }
        );
        
        // Highlight the problematic product
        const element = document.getElementById(`product-${i}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          element.classList.add('ring-2', 'ring-red-500');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-red-500');
          }, 3000);
        }
        
        return;
      }
    }
  }
  
  setUpdating(true);

  try {
    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        status: formData.status,
        delivery_price: formData.delivery_price,
        change_price: formData.change_price,
        comment: formData.comment,
        city: formData.city?.value,
        unlockable: formData.unlockable,
        change: formData.change,
        client: {
          name: formData.client.name,
          phone: `${formData.client.phone}`,
          address: formData.client.address,
          city: formData.client.city
        },
        products: formData.products.map(product => ({
          id: product.id,
          quantity: product.quantity,
          unit_price: product.unit_price, 
          selling_price: product.selling_price,
          variants: Object.values(product.selectedVariants || {}).map(variant => ({
            id: variant.id,
            type: variant.type
          }))
        }))
      })
    });

    const data = await response.json();

    if (!response.ok) throw new Error(data.message || t('orders.update_failed'));

    toast.success(t('orders.update_success'));
    
    const refreshResponse = await fetch(`${BASE_URL}/api/orders/${id}`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });
    
    if (!refreshResponse.ok) throw new Error(t('orders.fetch_updated_failed'));
    
    const refreshData = await refreshResponse.json();
    
    setOrder(refreshData.order);
    
    // Find city data
    const cityData = cities.find(c => c.name === refreshData.order.client.city) || {
      deliver: refreshData.order.delivery_price,
      change: refreshData.order.change_price || refreshData.order.delivery_price
    };
    
    setFormData({
      status: refreshData.order.status,
      delivery_price: parseFloat(refreshData.order.delivery_price),
      change_price: parseFloat(refreshData.order.change_price || refreshData.order.delivery_price),
      comment: refreshData.order.commentaire,
      city: { 
        label: refreshData.order.client.city,
        value: refreshData.order.client.city,
        deliver: parseFloat(cityData.deliver) || 0,
        change: parseFloat(cityData.change) || 0
      },
      unlockable: Boolean(Number(refreshData.order.unlockable)),
      change: Boolean(Number(refreshData.order.change)), 
      client: {
        name: refreshData.order.client.name,
        phone: refreshData.order.client.phone,
        address: refreshData.order.client.address,
        city: refreshData.order.client.city
      },
      products: refreshData.order.products.map(product => {
        const selectedVariants = {};
        product.variants.forEach(variantGroup => {
          variantGroup.values.forEach(variant => {
            selectedVariants[variantGroup.type] = {
              id: variant.id,
              name: variant.name,
              value: variant.value,
              type: variantGroup.type
            };
          });
        });

        return {
          id: product.id,
          name: product.name,
          image_py: product.image_py,
          quantity: product.quantity,
          unit_price: parseFloat(product.unit_price),
          selling_price: parseFloat(product.selling_price),
          variants: product.variants,
          allvariants: product.allvariants,
          selectedVariants: selectedVariants
        };
      })
    });
    
    setEditing(false);
  } catch (error) {
    toast.error(error.message);
  } finally {
    setUpdating(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header Skeleton */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="w-full md:w-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-48 bg-gray-200 rounded-lg animate-pulse" />
            </div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded-xl animate-pulse" />
        </div>

        <div className="grid lg:grid-cols-[60%_38%] gap-6">
          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-lg p-6 max-h-max">
            {/* Order Details Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse" />
            </div>

            <div className="space-y-6">
              {/* Client Info Section */}
              <div className="border-b border-gray-300 pb-4">
                <div className="h-5 w-20 bg-gray-200 rounded animate-pulse mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i}>
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                      <div className="h-10 w-full bg-gray-100 rounded-2xl animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Items Section */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="h-10 w-64 bg-gray-100 rounded-2xl animate-pulse" />
                </div>
                <div className="space-y-4">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="flex items-start gap-4 p-3 border border-gray-300 rounded-xl">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                        </div>
                        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                        <div className="flex gap-2">
                          {[...Array(3)].map((_, j) => (
                            <div key={j} className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="h-5 w-20 bg-gray-200 rounded animate-pulse mb-4" />
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex justify-between">
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Confirmator Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-2xl animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-10 w-24 bg-gray-200 rounded-xl animate-pulse flex-shrink-0" />
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>

            {/* Order Status Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            </div>

            {/* Comment Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-4/6 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>

            {/* Timeline Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="h-6 w-24 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse" />
                      {i !== 2 && <div className="w-px h-10 bg-gray-200" />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-48 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p>{t('orders.load_failed')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Package size={35} className="text-[var(--color-primary-500)]" />
              {t('orders.order_label')} #{order.code}
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              {t('orders.created_at')} {new Date(order.created_at).toLocaleDateString(locale === 'ar' ? 'ar-MA' : 'fr-FR')} {new Date(order.created_at).toLocaleTimeString(locale === 'ar' ? 'ar-MA' : 'fr-FR')}
            </p>
          </div>
          <div>
            {
                       (role === 0 && (order.status === 'nouveau' || order.status === 'nonconfirme' || order.status === 'reporte' )) 
                    || (role === 3 && (order.status === 'nouveau' 
                                  || order.status === 'nonconfirme' 
                                  || order.status === 'reporte' 
                                  || order.status === 'confirme' 
                                  || order.status === 'annule' 
                                  || order.status === 'reporte' 
                                  || order.status === 'refuse' ))
                    || (role === 1 )
                    || (role === 2 && roles.update_order === 1 && (
                                     order.status === 'nouveau' 
                                  || order.status === 'nonconfirme' 
                                  || order.status === 'reporte' 
                                  || order.status === 'confirme' 
                                  || order.status === 'annule' 
                                  || order.status === 'reporte' 
                                  || order.status === 'refuse' )) ?
                <div>
                  {!editing ? (
                    <button
                      onClick={() => {setEditing(true); fetchData() }}
                      className="flex items-center text-sm cursor-pointer gap-2 bg-[var(--color-primary-500)] text-white px-4 py-2 rounded-xl hover:bg-[var(--color-primary-600)] transition-colors"
                    >
                      <Edit size={16} />
                      {t('buttons.edit')}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditing(false);
                          // Reset form with current order data
                          const cityData = cities.find(c => c.name === order.client.city) || {
                            deliver: order.delivery_price,
                            change: order.change_price || order.delivery_price
                          };
                          
                          setFormData({
                            status: order.status,
                            delivery_price: parseFloat(order.delivery_price),
                            change_price: parseFloat(order.change_price || order.delivery_price),
                            comment: order.commentaire,
                            city: { 
                              label: order.client.city,
                              value: order.client.city,
                              deliver: parseFloat(cityData.deliver) || 0,
                              change: parseFloat(cityData.change) || 0
                            },
                            unlockable: order.unlockable,
                            change: order.change,
                            client: {
                              name: order.client.name,
                              phone: order.client.phone,
                              address: order.client.address,
                              city: order.client.city
                            },
                            products: order.products.map(product => {
                              const selectedVariants = {};
                              product.variants.forEach(variantGroup => {
                                variantGroup.values.forEach(variant => {
                                  selectedVariants[variantGroup.type] = {
                                    id: variant.id,
                                    name: variant.name,
                                    value: variant.value,
                                    type: variantGroup.type
                                  };
                                });
                              });
        
                              return {
                                id: product.id,
                                name: product.name,
                                image_py: product.image_py,
                                quantity: product.quantity,
                                unit_price: parseFloat(product.unit_price),
                                selling_price: parseFloat(product.selling_price),
                                variants: product.variants,
                                allvariants: product.allvariants,
                                selectedVariants: selectedVariants
                              };
                            })
                          });
                        }}
                        className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 cursor-pointer rounded-xl text-sm hover:bg-gray-300 transition-colors"
                      >
                        <X size={16} />
                        {t('buttons.cancel')}
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={updating}
                        className="flex items-center gap-2 bg-[var(--color-primary-500)] text-white px-4 py-2 cursor-pointer rounded-xl text-sm hover:bg-[#099c89] transition-colors disabled:opacity-70"
                      >
                        {updating ? (
                          <Loader2 className="animate-spin h-4 w-4" />
                        ) : (
                          <Save size={16} />
                        )}
                        {updating ? t('buttons.updating') : t('buttons.save')}
                      </button>
                    </div>
                  )}
                </div>
                :
                ''
            }
          </div>
        </div>

        <div className="grid lg:grid-cols-[60%_38%] gap-6">
          {/* Order Details */}
          <div className="bg-white rounded-2xl shadow-lg p-6 max-h-max">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-bold text-gray-900">
                {t('orders.order_details')}
              </h2>
              <span className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                getStatusColor(order.status)
              }`}>
                {getStatusIcon(order.status)}
                {getStatusText(t('status.'+order.status))}
              </span>
            </div>

            <div className="space-y-6">
              {/* Client Info */}
              <div className="border-b border-gray-300 pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('orders.client')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm dark:text-white">{t('orders.name')}</p>
                    {editing ? (
                      <input
                        type="text"
                        name="name"
                        value={formData.client.name}
                        onChange={handleClientChange}
                        className="w-full py-3 px-4 mt-1 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      />
                    ) : (
                      <p className="font-medium text-xs text-gray-500 mt-1">{order.client.name}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">{t('orders.phone')}</p>
                    {editing ? (
                      <input
                        type="text"
                        name="phone"
                        value={formData.client.phone}
                        onChange={handleClientChange}
                        className="w-full py-3 px-4 mt-1 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      />
                    ) : (
                      <p className="font-medium text-xs text-gray-500 mt-1">{order.client.phone}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">{t('orders.city')}</p>
                    {editing ? (
                      <Select
                        options={cityOptions}
                        value={cityOptions.find(option => option.value === formData.city?.value)}
                        onChange={handleCityChange}
                        placeholder={t('orders.select_city')}
                        className={`w-full py-0.5 px-1 mt-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                            cursor: 'pointer',
                          }),
                        }}
                        isSearchable
                      />
                    ) : (
                      <p className="font-medium text-xs text-gray-500 mt-1">{order.client.city}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">{t('orders.address')}</p>
                    {editing ? (
                      <input
                        type="text"
                        name="address"
                        value={formData.client.address}
                        onChange={handleClientChange}
                        className="w-full py-3 px-4 mt-1 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      />
                    ) : (
                      <p className="font-medium text-xs text-gray-500 mt-1">{order.client.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900">{t('orders.articles')} ({formData.products.length})</h3>
                  {editing && (
                    <div className="w-64">
                      <Select
                        options={productOptions}
                        onChange={handleAddProduct}
                        placeholder={t('orders.add_product')}
                        className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                            cursor: 'pointer',
                          }),
                        }}
                        isClearable={false}
                        isSearchable
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {formData.products.map((product, index) => {
                    const formattedVariants = formatVariants(product.variants);
                    const currentProduct = formData.products[index] || product;
                    const transformedVariants = transformAllVariants(product.allvariants);
                    
                    return (
                      <div key={index} className="flex items-start gap-4 p-3 border border-gray-300 rounded-xl">
                        <img
                          src={`${BASE_URL}/storage/${product.image_py}`}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://placehold.co/80x80";
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <h4 className="font-medium text-xs dark:text-white">{product.name}</h4>
                            {editing && (
                              <button 
                                onClick={() => handleRemoveProduct(index)}
                                className="text-red-500 cursor-pointer hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <div className="space-x-16">
                                <span className="text-[var(--color-primary-500)] text-xs font-bold">{product.unit_price.toFixed(2)} MAD</span>
                              </div>
                            </div>
                            <span>
                              {editing ? (
                                <>
                                  <input
                                    type="number"
                                    value={currentProduct.quantity}
                                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                    className="w-28 py-1 px-4 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                                    min="1"
                                  />
                                  <span>×</span>
                                  <input
                                    type="number"
                                    value={currentProduct.selling_price}
                                    onChange={(e) => handleProductChange(index, 'selling_price', e.target.value)}
                                    className="w-28 py-1 px-4 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                                    step="0.01"
                                    min="0"
                                  />
                                </>
                              ) : (
                                <span>{product.quantity} × {product.selling_price} MAD</span>
                              )}
                              <span className="font-semibold"> = {(currentProduct.quantity * currentProduct.selling_price).toFixed(2)} MAD</span>
                            </span>
                          </div>
                          
                          {editing && transformedVariants.length > 0 && (
                            <div className="mt-1">
                              <VariantSelector
                                variants={transformedVariants}
                                onVariantChange={handleVariantChange}
                                initialSelectedVariants={currentProduct.selectedVariants || {}}
                                index={index}
                              />
                            </div>
                          )}
                          
                          {!editing && formattedVariants.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {formattedVariants
                                .sort((a, b) => {
                                  // Put color variants first, then sort others alphabetically by type
                                  if (a.type === 'color') return -1;
                                  if (b.type === 'color') return 1;
                                  return a.type.localeCompare(b.type);
                                })
                                .map((variant, vIndex) => (
                                  <span 
                                    key={vIndex} 
                                    className="text-xs dark:text-white px-2 py-1 bg-gray-100 rounded-full flex items-center gap-1"
                                  >
                                    {variant.type === 'color' && (
                                      <span 
                                        className="w-3 h-3 rounded-full inline-block border border-gray-200" 
                                        style={{ backgroundColor: variant.value }}
                                      />
                                    )}
                                    {variant.name}
                                  </span>
                                ))}
                            </div>
                          )}

                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">{t('orders.summary')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('orders.subtotal')} ({formData.products.length} {t('orders.articles')})</span>
                    <span className="text-sm dark:text-white">
                      {subtotal.toFixed(2)} MAD
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('orders.delivery')}</span>
                    <span className="text-sm dark:text-white">{formData.delivery_price} MAD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('orders.profit')}</span>
                    <span className="text-sm text-green-600">
                      {profit.toFixed(2)} MAD
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="font-medium dark:text-white">{t('orders.total')}</span>
                    <span className="font-bold text-[var(--color-primary-500)]">
                      {total.toFixed(2)} MAD
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 ">

          

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] rounded-2xl p-3 shadow-sm">
                  <Headset size={32} className="text-[var(--color-primary-500)]" />
                </div>

                {order?.confirmator ? (
                  <>
                    <div>
                      <p className="text-xs font-medium text-gray-500">{t('orders.confirmation_agent')}</p>
                      <h3 className="text-xl font-bold text-gray-800">
                        {order.confirmator.name}
                      </h3>

                      <div className="flex items-center mt-1">
                        <span className="flex w-2 h-2 mr-2">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs text-green-600">{t('orders.available')}</span>
                      </div>
                    </div>

                    <Link 
                        href={`https://wa.me/212${order.confirmator.phone}`} 
                        target="_blank"
                        className="ml-auto cursor-pointer flex items-center gap-2 bg-[var(--color-primary-50)] hover:bg-[var(--color-primary-100)] transition-colors text-[var(--color-primary-500)] font-medium py-2 px-4 rounded-xl text-sm">
                      <FaWhatsapp size={20} />
                      {t('orders.contact')}
                    </Link>
                  </>
                ) : (
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">
                      {t('orders.no_agent')}
                    </p>
                  </div>
                )}
              </div>

              {order?.confirmator && (
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-1">
                    <Phone size={15} className="text-gray-500" />
                    <p className="text-xs text-gray-500">{t('orders.phone_label')} +212 {order.confirmator.phone}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('orders.order_status')}
              </h2>
              
              {editing && role === 1 ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orders.status')}
                    </label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(opt => opt.value === formData.status)}
                      onChange={handleStatusChange}
                      className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="order-change"
                      checked={formData.change}
                      onChange={handleChangeToggle}
                      className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                    />
                    <label htmlFor="order-change" className="text-sm text-gray-700 cursor-pointer">
                      {t('orders.change')}
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="order-unlockable"
                      checked={formData.unlockable}
                      onChange={(e) => setFormData(prev => ({ ...prev, unlockable: e.target.checked }))}
                      className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                    />
                    <label htmlFor="order-unlockable" className="text-sm text-gray-700 cursor-pointer">
                      {t('orders.unlockable')}
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                    <div className="flex items-center gap-1">
                      <div className={`py-0.5 px-1.5 gap-1 flex items-center justfiy-center text-xs rounded-full max-w-max ${getStatusColor(order.status)}`} >
                      {getStatusIcon(order.status)}
                      {getStatusText(t('status.'+order.status))}
                      </div>
                    </div>
                  
                  <div className="flex items-center gap-3 text-sm">
                    {order.change ? (
                      <CheckCircle className="text-green-500 h-4 w-4" />
                    ) : (
                      <X className="text-gray-400 h-4 w-4" />
                    )}
                    <span className="dark:text-white">{t('orders.change')}: {order.change ? t('orders.yes') : t('orders.no')}</span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm">
                    {order.unlockable ? (
                      <CheckCircle className="text-green-500 h-4 w-4" />
                    ) : (
                      <X className="text-gray-400 h-4 w-4" />
                    )}
                    <span className="dark:text-white">{t('orders.unlockable')}: {order.unlockable ? t('orders.yes') : t('orders.no')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Comment Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('orders.comment')}
              </h2>

              {editing ? (
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  className="w-full py-3 px-4 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  placeholder={t('orders.add_comment')}
                />
              ) : (
                <p className={`text-sm ${order.commentaire ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                  {order.commentaire || t('orders.no_comment')}
                </p>
              )}
            </div>

            <div className="">
              {
                order.city_coordinates && (
                <MapDirection city={order.client.city} coordinates={order.city_coordinates.coordinates} />
                )
              }
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('orders.history')}
              </h2>

              <div className="space-y-1">
                {order.status_history?.map((history, index) => ( 
                  <div key={history.id} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`p-0.5 rounded-full`}
                      />
                        <div className={`${getStatusColor(history.status)} flex items-center justify-center gap-1 py-1 px-2 pr-2 text-xs pr-0 rounded-full`}>
                          {getStatusIcon(history.status)}
                          {getStatusText(t('status.'+history.status))}
                        </div>
                        {index !== order.status_history.length - 1 && (
                      <div className="w-px h-10 bg-gray-200" />
                      )}
                    </div>

                    <div>
                      <p className="font-medium text-sm">
                        {getStatusText(history.status)}
                      </p>
                      {history.raison && history.raison !== "Autre" &&  (
                        <p className="text-gray-500 text-xs">{t('orders.reason')} {history.raison}</p>
                      )}
                      {
                          history.comment && (history.status === 'reporte' || history.status === 'Reporté par livreur') ?
                          <p className="text-gray-500 text-[11px]">{t('orders.postponed_to')} {history.comment}</p>
                          : history.comment !== null ?
                          <p className="text-gray-500 text-[11px]">{t('orders.comment_label')} {history.comment}</p>
                         :''
                      }
                      <p className="text-gray-500 text-[10px]">
                        {new Date(history.created_at).toLocaleString(locale === 'ar' ? 'ar-MA' : 'fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

